import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FaBox, FaSync, FaEye, FaExclamationTriangle, FaLock } from 'react-icons/fa';
import '../styles/AdminOrders.css';

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [notLoggedIn, setNotLoggedIn] = useState(false);
  const [filter, setFilter] = useState('pending_processing');
  const [pagination, setPagination] = useState({ limit: 50, offset: 0, total: 0 });
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    checkAccess();
  }, []);

  const checkAccess = async () => {
    try {
      const res = await axios.get('https://reanimeitor.com/backend/profile.php', { withCredentials: true });
      if (res.data.success && res.data.user) {
        if (res.data.user.role === 'sys_admin') {
          loadOrders();
        } else {
          setAccessDenied(true);
          setLoading(false);
        }
      } else {
        setNotLoggedIn(true);
        setLoading(false);
      }
    } catch (err) {
      setNotLoggedIn(true);
      setLoading(false);
    }
  };

  const loadOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        limit: pagination.limit,
        offset: pagination.offset,
        filter: filter
      });
      const res = await axios.get(`https://reanimeitor.com/backend/get_orders.php?${params}`, { withCredentials: true });
      if (res.data.success) {
        setOrders(res.data.orders);
        setPagination(prev => ({ ...prev, total: res.data.total }));
      } else {
        setError('Error al cargar pedidos');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const updateShippingStatus = async (orderId, newStatus) => {
    setUpdatingStatus(true);
    try {
      const res = await axios.post(
        'https://reanimeitor.com/backend/update_shipping_status.php',
        { order_id: orderId, shipping_status: newStatus },
        { withCredentials: true }
      );
      if (res.data.success) {
        loadOrders();
      } else {
        alert('Error al actualizar estado');
      }
    } catch (err) {
      alert('Error de conexión');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleFilterChange = (e) => {
    const newFilter = e.target.value;
    setFilter(newFilter);
    setPagination(prev => ({ ...prev, offset: 0 }));
  };

  // Ejecutar loadOrders cuando cambie el filtro o la paginación
  useEffect(() => {
    if (filter) {
      loadOrders();
    }
  }, [filter, pagination.offset]);

  const handlePageChange = (newOffset) => {
    setPagination(prev => ({ ...prev, offset: newOffset }));
  };

  const resetFilters = () => {
    setFilter('pending_processing');
    setPagination(prev => ({ ...prev, offset: 0 }));
  };

  const viewOrderDetails = (order) => {
    setSelectedOrder(order);
    setShowDetailModal(true);
  };

  const getShippingStatusText = (status) => {
    switch (status) {
      case 'pending': return '📦 Pendiente';
      case 'processing': return '🚚 En proceso';
      case 'completed': return '✅ Completado';
      default: return status;
    }
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  const formatPrice = (p) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(p);
  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;

  if (loading) return <div className="admin-loading"><div className="spinner"></div><p>Cargando panel...</p></div>;
  if (notLoggedIn) return (
    <div className="admin-access-denied">
      <FaLock className="denied-icon" />
      <h2>Acceso restringido</h2>
      <p>Debes iniciar sesión para acceder.</p>
      <button onClick={() => navigate('/login')} className="btn-primary">Iniciar sesión</button>
    </div>
  );
  if (accessDenied) return (
    <div className="admin-access-denied">
      <FaExclamationTriangle className="denied-icon" />
      <h2>Acceso denegado</h2>
      <p>No tienes permisos de administrador.</p>
      <button onClick={() => navigate('/')} className="btn-primary">Volver al inicio</button>
    </div>
  );

  return (
    <div className="admin-orders-container">
      <div className="admin-header">
        <h1><FaBox /> Gestión de Pedidos - Envíos</h1>
      </div>

      {/* Filtros */}
      <div className="filters-card">
        <div className="filters-row">
          <div className="filter-group">
            <label>Mostrar pedidos:</label>
            <select value={filter} onChange={handleFilterChange}>
              <option value="pending_processing">📦 Pendientes + En proceso</option>
              <option value="completed">✅ Completados</option>
              <option value="all">📋 Todos</option>
            </select>
          </div>
          <div className="filter-actions">
            <button className="btn-secondary" onClick={resetFilters}>
              <FaSync /> Limpiar filtros
            </button>
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div className="orders-table-container">
        {error ? (
          <div className="error-message">{error}<button onClick={loadOrders}>Reintentar</button></div>
        ) : orders.length === 0 ? (
          <div className="empty-state"><FaBox /><p>No hay pedidos con el filtro seleccionado</p></div>
        ) : (
          <>
            <table className="orders-table">
              <thead>
                <tr><th>Nº Pedido</th><th>Cliente</th><th>Pack</th><th>Total</th><th>BGP</th><th>Estado Envío</th><th>Fecha</th><th>Acciones</th></tr>
              </thead>
              <tbody>
                {orders.map(o => (
                  <tr key={o.id}>
                    <td>{o.order_number}</td>
                    <td><strong>{o.customer_name || o.user_name}</strong><br/><small>{o.customer_email || o.user_email}</small></td>
                    <td>{o.pack_name}</td>
                    <td>{formatPrice(o.amount_eur)}</td>
                    <td>{o.bgp_tokens.toLocaleString()}</td>
                    <td>
                      <select 
                        value={o.shipping_status} 
                        onChange={(e) => updateShippingStatus(o.id, e.target.value)}
                        disabled={updatingStatus}
                        className="shipping-status-select"
                      >
                        <option value="pending">📦 Pendiente</option>
                        <option value="processing">🚚 En proceso</option>
                        <option value="completed">✅ Completado</option>
                      </select>
                    </td>
                    <td>{formatDate(o.created_at)}</td>
                    <td><button className="view-btn" onClick={() => viewOrderDetails(o)}><FaEye /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div className="pagination">
                <button disabled={pagination.offset===0} onClick={()=>handlePageChange(pagination.offset-pagination.limit)}>Anterior</button>
                <span>Página {currentPage} de {totalPages}</span>
                <button disabled={pagination.offset+pagination.limit>=pagination.total} onClick={()=>handlePageChange(pagination.offset+pagination.limit)}>Siguiente</button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal de detalles */}
      {showDetailModal && selectedOrder && (
        <div className="modal-overlay" onClick={()=>setShowDetailModal(false)}>
          <div className="modal-content" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h2>Detalles del Pedido</h2>
              <button className="modal-close" onClick={()=>setShowDetailModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div><strong>Nº Pedido:</strong> {selectedOrder.order_number}</div>
              <div><strong>Cliente:</strong> {selectedOrder.customer_name || selectedOrder.user_name}</div>
              <div><strong>Email:</strong> {selectedOrder.customer_email || selectedOrder.user_email}</div>
              <div><strong>Teléfono:</strong> {selectedOrder.customer_phone || '—'}</div>
              <div><strong>Dirección:</strong> {selectedOrder.customer_address ? `${selectedOrder.customer_address}, ${selectedOrder.customer_city}, ${selectedOrder.customer_postal_code}` : '—'}</div>
              <div><strong>Pack:</strong> {selectedOrder.pack_name}</div>
              <div><strong>Total:</strong> {formatPrice(selectedOrder.amount_eur)}</div>
              <div><strong>BGP:</strong> {selectedOrder.bgp_tokens.toLocaleString()}</div>
              <div><strong>Estado envío:</strong> {getShippingStatusText(selectedOrder.shipping_status)}</div>
              <div><strong>Fecha:</strong> {formatDate(selectedOrder.created_at)}</div>
            </div>
            <div className="modal-footer">
              <button onClick={()=>setShowDetailModal(false)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;